import { useNavigate } from 'react-router-dom'

import {
  Box,
  Button,
  Icon,
  SelectOption,
  Spinner,
  Tooltip,
  useToggle,
} from '@factorialco/deprecated-design-system'
import {
  AccessForSelectFragment,
  AtsNewJobRequisitionModalPageDocument,
  AtsRequisitionReasonsEnum,
} from 'generated/resources/graphql'

import { AccessSelector } from 'modules/core/components/AccessSelector'
import Modal from 'modules/core/components/Modal'
import Select from 'modules/core/components/Select'
import TextArea from 'modules/core/components/TextArea'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useEffect } from 'modules/core/hooks/useEffect'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useCreateRequisition } from 'modules/ats/hooks/requisitions/useCreateRequisition'

interface RequisitionFormValues {
  jobPostingTitle: string
  workplaceId: string
  teamId?: string
  companyId: number
  authorId: number
  hiringManager: AccessForSelectFragment | null
  recruiter: AccessForSelectFragment | null
  reason: AtsRequisitionReasonsEnum
  note: string
}

const NewJobRequisitionModalPage = () => {
  const { company, access } = useCurrentSession()
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const [openReasonToolTip, toggleOpenReasonToolTip] = useToggle()
  const { loading, data } = useQuery(AtsNewJobRequisitionModalPageDocument, {})

  const onClose = () => {
    track(trackingEvents.ats.requestJobOpeningCancel)
    navigate(navigation.ats.jobs.requisitions.fullPath())
  }

  useEffect(() => {
    track(trackingEvents.ats.requestJobOpeningStart)
  }, [])
  const { create } = useCreateRequisition()

  const teamsOptions: SelectOption[] =
    data?.teams.teams
      ?.filter((team) => team.name)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((team) => ({ label: team.name, value: team.id })) || []

  const locationOptions: SelectOption[] =
    data?.locations.locations
      ?.filter((location) => location.name)
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((location) => ({ label: location.name, value: location.id })) || []

  const setFlashMessage = (success: boolean) => {
    const type = success ? 'correct' : 'wrong'
    const message = success
      ? i18n.t('ats.requisitions_form.success')
      : i18n.t('ats.requisitions_form.error')

    app.setFlash({
      type: type,
      message: message,
      time: 2000,
    })
  }

  const reasonOptions = [
    {
      label: i18n.t('ats.requisitions_form.reason.options.new_position'),
      value: 'new_position',
    },
    {
      label: i18n.t('ats.requisitions_form.reason.options.backfill'),
      value: 'backfill',
    },
  ]

  const { values, setValues, isSubmitting, touched, errors, isValid, handleSubmit } =
    useForm<RequisitionFormValues>({
      defaultValues: {
        jobPostingTitle: '',
        reason: 'new_position',
        note: '',
        hiringManager: access,
        recruiter: null,
        workplaceId: '',
        authorId: access.id,
        companyId: company.id,
      },
      validate: (formData) => {
        const formErrors: FormErrors<RequisitionFormValues> = {}

        if (!formData.jobPostingTitle) {
          formErrors.jobPostingTitle = i18n.t('common.validations.empty')
        }
        if (!formData.hiringManager) {
          formErrors.hiringManager = i18n.t('common.validations.empty')
        }
        if (!formData.recruiter) {
          formErrors.recruiter = i18n.t('common.validations.empty')
        }
        if (!formData.workplaceId) {
          formErrors.workplaceId = i18n.t('common.validations.empty')
        }
        if (!formData.note) {
          formErrors.note = i18n.t('common.validations.empty')
        }

        return formErrors
      },
      onSubmit: async (formData, { setErrors }) => {
        const variables = {
          jobPostingTitle: formData.jobPostingTitle,
          reason: formData.reason,
          note: formData.note,
          hiringManagerId: Number(formData.hiringManager?.id),
          recruiterId: Number(formData.recruiter?.id),
          workplaceId: Number(formData.workplaceId),
          teamId: formData.teamId ? Number(formData.teamId) : null,
          authorId: access.id,
          companyId: company.id,
        }

        const result = await create(variables)
        const handled = handleResult(result, (resultErrors) => {
          setErrors(resultErrors)
        })

        track(trackingEvents.ats.requestJobOpeningSubmit)
        setFlashMessage(handled.success)
        onClose()

        return handled
      },
    })

  return (
    <Modal
      illustration="document-handshake"
      title={i18n.t('ats.requisitions_form.title')}
      onClose={onClose}
      onSubmit={() => handleSubmit()}
      footer={
        <Button disabled={isSubmitting || !isValid} submit>
          {i18n.t('ats.requisitions_form.send_request')}
        </Button>
      }
    >
      {loading || isSubmitting ? (
        <Box padding="s40" width="full" alignItems="center">
          <Spinner />
        </Box>
      ) : (
        <Box gap="s24">
          <TextField
            label={i18n.t('ats.requisitions_form.job_title')}
            value={values.jobPostingTitle}
            onChange={(jobPostingTitle) => setValues({ ...values, jobPostingTitle })}
            error={touched.jobPostingTitle ? errors.jobPostingTitle : undefined}
            mandatory
          />
          <Box flexDirection="row" gap="s12">
            <Select
              mandatory
              none={i18n.t('common.select')}
              label={i18n.t('ats.requisitions_form.workplace')}
              options={locationOptions}
              error={touched.workplaceId ? errors.workplaceId : undefined}
              value={values.workplaceId}
              onChange={(workplaceId) => setValues({ ...values, workplaceId })}
            />
            <Select
              none={i18n.t('common.select')}
              label={i18n.t('ats.requisitions_form.team')}
              options={teamsOptions}
              error={touched.teamId ? errors.teamId : undefined}
              onChange={(teamId) => setValues({ ...values, teamId })}
              value={values.teamId}
            />
          </Box>
          <AccessSelector
            mandatory
            label={i18n.t('ats.requisitions_form.hiring_manager')}
            description={i18n.t('ats.requisitions_form.hiring_manager_description')}
            error={touched.hiringManager ? errors.hiringManager : undefined}
            value={values.hiringManager}
            onSelect={(hiringManager) => setValues({ ...values, hiringManager })}
            multiple={false}
            terminated={false}
            width="s400"
          />
          <AccessSelector
            mandatory
            label={i18n.t('ats.requisitions_form.recruiter')}
            description={i18n.t('ats.requisitions_form.recruiter_description')}
            error={touched.recruiter ? errors.recruiter : undefined}
            value={values.recruiter}
            onSelect={(recruiter) => setValues({ ...values, recruiter })}
            multiple={false}
            terminated={false}
            width="s400"
          />
          <Select
            mandatory
            none={i18n.t('common.select')}
            label={i18n.t('ats.requisitions_form.reason.label')}
            options={reasonOptions}
            error={touched.reason ? errors.reason : undefined}
            value={values.reason}
            onChange={(reason) =>
              setValues({ ...values, reason: reason as AtsRequisitionReasonsEnum })
            }
            secondaryLabel={
              <Tooltip.Alt
                open={openReasonToolTip}
                toggle={toggleOpenReasonToolTip}
                label={i18n.t('ats.requisitions_form.reason.tooltip')}
                attachment="bottom center"
              >
                <Icon.Small icon="question" encircled="grey800" />
              </Tooltip.Alt>
            }
          />
          <TextArea
            mandatory
            label={i18n.t('ats.requisitions_form.note')}
            value={values.note}
            onChange={(note) => setValues({ ...values, note })}
            error={touched.note ? errors.note : undefined}
          />
        </Box>
      )}
    </Modal>
  )
}

export default NewJobRequisitionModalPage
